import { useCallback, useState } from 'react';
import {
  ExceptionLogicOperatorAll,
  ExceptionLogicOperatorAny,
  ExceptionResourceTypeOperatory,
  ExceptionResourceTypeProvider,
  ExceptionResourceTypeService,
  ExceptionScopeTypeLocation,
  ExceptionScopeTypeResource,
  ScheduleException,
} from '@weave/schema-gen-ts/dist/schemas/schedule/v3/exception.pb';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { RRule } from 'rrule';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useTranslation } from '@frontend/i18n';
import { formatDateTime } from '@frontend/schedule-helpers';
import { formatDate, useAlert, useForm, UseFormReturnType } from '@frontend/design-system';
import { AvailabilityRecurrenceTypes } from '../../../../components/AvailabilityRules/AvailabilityRuleForm/types';
import { DATE_DISPLAY_FORMAT } from '../../../../utils/date-helpers';

dayjs.extend(utc);

const { DATETIME_FORMATS } = SchedulerV3.Helpers;
const ALL_DAY_START_TIME = '00:00:00';
const ALL_DAY_END_TIME = '23:59:00';

export type RuleExceptionFormTarget = 'ALL_RESOURCES' | 'SPECIFIC_RESOURCES' | 'RESOURCE_COMBINATION';

export type RuleExceptionFormConfig = {
  name: { type: 'text'; value: string; required: boolean };
  isRecurring: { type: 'switch'; value: boolean };
  recurrenceType: { type: 'dropdown'; value: AvailabilityRecurrenceTypes; required: boolean };
  // MM/DD/YYYY format for date fields to be compatible with the date input value format
  startDate: { type: 'date'; value: string; required: boolean };
  endDate: { type: 'date'; value: string; required: boolean };
  // HH:mm:ss format for time fields to be compatible with the time input value format
  startTime: { type: 'time'; value: string; required: boolean };
  endTime: { type: 'time'; value: string; required: boolean };
  // All day event
  isAllDay: { type: 'checkbox'; value: boolean };
  // Target (scope) fields - 'ALL_RESOURCES' | 'SPECIFIC_RESOURCES' | 'RESOURCE_COMBINATION'
  target: { type: 'radio'; value: RuleExceptionFormTarget; required: boolean };
  logicOperator: { type: 'dropdown'; value: string };
  // SPECIFIC_RESOURCES multi-select fields
  providers: { type: 'checklist'; value: string[] };
  operatories: { type: 'checklist'; value: string[] };
  services: { type: 'checklist'; value: string[] };
  // RESOURCE_COMBINATION single-select fields
  provider: { type: 'dropdown'; value: string };
  operatory: { type: 'dropdown'; value: string };
  service: { type: 'dropdown'; value: string };
};

export type GetRuleExceptionFormFieldProps = UseFormReturnType<
  RuleExceptionFormConfig,
  keyof RuleExceptionFormConfig
>['getFieldProps'];

export type RuleExceptionFormValues = UseFormReturnType<
  RuleExceptionFormConfig,
  keyof RuleExceptionFormConfig
>['values'];

type UseAvailabilityRuleFormProps = {
  selectedException: ScheduleException | null;
  locationId: string;
  isEditMode: boolean;
  closeModal: () => void;
};

export const useAddEditRuleExceptionForm = ({
  selectedException,
  locationId,
  isEditMode,
  closeModal,
}: UseAvailabilityRuleFormProps) => {
  const { t } = useTranslation('schedule-settings');
  const alert = useAlert();
  const [rRule, setRRule] = useState<RRule | undefined>(undefined);
  const { locationTimezoneMap } = SchedulerV3.Helpers.useScheduleLocationTimezoneShallowStore('locationTimezoneMap');
  const timezone = locationTimezoneMap[locationId]?.timezone;
  const todayDate = dayjs().format(DATE_DISPLAY_FORMAT);
  const initialTarget = getInitialTarget(selectedException);
  const initialStartTime = initialTimeFormatter(selectedException?.time?.startTime, timezone) || '';
  const initialEndTime = initialTimeFormatter(selectedException?.time?.endTime, timezone) || '';

  const { isComplete, values, getFieldProps, reset, seedValues } = useForm<
    RuleExceptionFormConfig,
    keyof RuleExceptionFormConfig
  >({
    fields: {
      name: {
        type: 'text',
        value: selectedException?.name || '',
        required: true,
      },
      isRecurring: {
        type: 'switch',
        value: !!(selectedException?.recurrenceType && selectedException.recurrenceType !== 'SINGLE_OCCURRENCE'),
      },
      recurrenceType: {
        type: 'dropdown',
        value: getInitialRecurrenceType(selectedException?.recurrenceType),
        required: true,
      },
      startDate: {
        type: 'date',
        value: initialDateFormatter(selectedException?.nextOccurrenceStart, timezone) || todayDate,
        required: true,
      },
      endDate: {
        type: 'date',
        value: initialDateFormatter(selectedException?.endDate, timezone) || todayDate,
        required: true,
      },
      startTime: {
        type: 'time',
        value: initialStartTime,
        required: false,
      },
      endTime: {
        type: 'time',
        value: initialEndTime,
        required: false,
      },
      isAllDay: {
        type: 'checkbox',
        value: initialStartTime === '00:00:00' && initialEndTime === '23:59:00',
      },
      target: {
        type: 'radio',
        value: initialTarget,
        required: true,
      },
      logicOperator: {
        type: 'dropdown',
        value: selectedException?.target?.logicOperator || '',
      },
      providers: {
        type: 'checklist',
        value:
          selectedException?.target?.resources
            ?.filter((r) => r.type === ExceptionResourceTypeProvider)
            .map((r) => r.id ?? '')
            .filter(Boolean) || [],
      },
      operatories: {
        type: 'checklist',
        value:
          selectedException?.target?.resources
            ?.filter((r) => r.type === ExceptionResourceTypeOperatory)
            .map((r) => r.id ?? '')
            .filter(Boolean) || [],
      },
      services: {
        type: 'checklist',
        value:
          selectedException?.target?.resources
            ?.filter((r) => r.type === ExceptionResourceTypeService)
            .map((r) => r.id ?? '')
            .filter(Boolean) || [],
      },
      provider: {
        type: 'dropdown',
        value: selectedException?.target?.resources?.find((r) => r.type === ExceptionResourceTypeProvider)?.id || '',
      },
      operatory: {
        type: 'dropdown',
        value: selectedException?.target?.resources?.find((r) => r.type === ExceptionResourceTypeOperatory)?.id || '',
      },
      service: {
        type: 'dropdown',
        value: selectedException?.target?.resources?.find((r) => r.type === ExceptionResourceTypeService)?.id || '',
      },
    },
  });

  const createExceptionMutation = SchedulerV3.Mutations.useCreateScheduleExceptionMutation({
    options: {
      onSuccess: () => {
        alert.success(t('Exception created successfully'));
        closeModal();
      },
      onError: () => {
        alert.error(t('Failed to create exception'));
      },
    },
  });

  const updateExceptionMutation = SchedulerV3.Mutations.useUpdateScheduleExceptionMutation({
    options: {
      onSuccess: () => {
        alert.success(t('Exception updated successfully'));
        closeModal();
      },
      onError: () => {
        alert.error(t('Failed to update exception'));
      },
    },
  });

  const handleTargetChange = (newTarget: RuleExceptionFormConfig['target']['value']) => {
    seedValues({ target: newTarget });
  };

  const handleSubmit = async () => {
    const startTime = values.isAllDay ? ALL_DAY_START_TIME : values.startTime;
    const endTime = values.isAllDay ? ALL_DAY_END_TIME : values.endTime;

    const recurrenceRule = rRule ? rRule.toString().replace(/^RRULE:/, '') : undefined;

    const isLocationScope = values.target === 'ALL_RESOURCES';
    const isCombination = values.target === 'RESOURCE_COMBINATION';

    const scopeType = isLocationScope ? ExceptionScopeTypeLocation : ExceptionScopeTypeResource;

    const logicOperator = isLocationScope
      ? undefined
      : isCombination
      ? ExceptionLogicOperatorAll
      : ExceptionLogicOperatorAny;

    const resources = isLocationScope
      ? undefined
      : isCombination
      ? [
          values.provider && { type: ExceptionResourceTypeProvider, id: values.provider },
          values.operatory && { type: ExceptionResourceTypeOperatory, id: values.operatory },
          values.service && { type: ExceptionResourceTypeService, id: values.service },
        ].filter((resource): resource is { type: 'PROVIDER' | 'OPERATORY' | 'SERVICE'; id: string } =>
          Boolean(resource)
        )
      : [
          ...(values.providers ?? []).map((id) => ({ type: ExceptionResourceTypeProvider, id })),
          ...(values.operatories ?? []).map((id) => ({ type: ExceptionResourceTypeOperatory, id })),
          ...(values.services ?? []).map((id) => ({ type: ExceptionResourceTypeService, id })),
        ];

    const commonPayload = {
      locationId,
      name: values.name || '',
      startDate: formatDate(values.startDate, DATETIME_FORMATS.ISO_DATE),
      ...(values.endDate && { endDate: formatDate(values.endDate, DATETIME_FORMATS.ISO_DATE) }),
      startTime: formatDateTime(startTime, {
        inputFormat: DATETIME_FORMATS.ISO_TIME,
        outputFormat: DATETIME_FORMATS.HOURS_MINUTES,
      }),
      endTime: formatDateTime(endTime, {
        inputFormat: DATETIME_FORMATS.ISO_TIME,
        outputFormat: DATETIME_FORMATS.HOURS_MINUTES,
      }),
      ...(values.isRecurring && { recurrenceType: values.recurrenceType }),
      ...(values.isRecurring && { recurrenceRule }),
      scopeType,
      logicOperator,
      resources,
      timezone,
    };

    if (isEditMode && selectedException?.id) {
      await updateExceptionMutation.mutateAsync({ ...commonPayload, id: selectedException.id });
    } else {
      await createExceptionMutation.mutateAsync(commonPayload);
    }
  };

  const handleRRuleChange = useCallback((nextRRule: RRule | undefined) => {
    setRRule(nextRRule);
  }, []);

  return {
    getFieldProps,
    values,
    isComplete,
    reset,
    handleSubmit,
    handleTargetChange,
    handleRRuleChange,
    rRule,
    seedValues,
    isSubmitting: createExceptionMutation.isLoading || updateExceptionMutation.isLoading,
  };
};

const getInitialRecurrenceType = (recurrenceType?: string): AvailabilityRecurrenceTypes => {
  if (!recurrenceType || recurrenceType === 'SINGLE_OCCURRENCE') {
    return 'SPECIFIC_DATE';
  }

  return recurrenceType as AvailabilityRecurrenceTypes;
};

const getInitialTarget = (
  selectedException: ScheduleException | null
): 'ALL_RESOURCES' | 'SPECIFIC_RESOURCES' | 'RESOURCE_COMBINATION' => {
  if (!selectedException?.target || selectedException.target.scopeType === ExceptionScopeTypeLocation) {
    return 'ALL_RESOURCES';
  }

  return selectedException.target.logicOperator === ExceptionLogicOperatorAll
    ? 'RESOURCE_COMBINATION'
    : 'SPECIFIC_RESOURCES';
};

/**
 * The API returns date and time in ISO format. To display the existing values in the form for editing,
 * we need to convert them to local date and time in MM/DD/YYYY and HH:mm format respectively based on the timezone of the location.
 */
const initialDateFormatter = (dateString?: string, timezone?: string): string => {
  return formatDateTime(dateString, {
    inputFormat: DATETIME_FORMATS.ISO_DATE,
    outputFormat: DATE_DISPLAY_FORMAT,
    toTimezone: timezone,
  });
};
const initialTimeFormatter = (timeString?: string, timezone?: string): string => {
  const formattedFromHoursMinutes = formatDateTime(timeString, {
    inputFormat: DATETIME_FORMATS.HOURS_MINUTES,
    outputFormat: DATETIME_FORMATS.ISO_TIME,
    toTimezone: timezone,
  });

  if (formattedFromHoursMinutes) {
    return formattedFromHoursMinutes;
  }

  // Fallback for sources that may already provide seconds.
  return formatDateTime(timeString, {
    inputFormat: DATETIME_FORMATS.ISO_TIME,
    outputFormat: DATETIME_FORMATS.ISO_TIME,
    toTimezone: timezone,
  });
};
